import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Text, Area, Toggle, Modal, SingleImageEditor } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

const empty = { key: "", name: "", description: "", example_image: null, price_note: "", price: "", enabled: true, scope: "per_design", all_designs: true, style_ids: [] };

export default function AdminAddons() {
  const [items, setItems] = useState([]);
  const [styles, setStyles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/admin/addons").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/styles").then((r) => setStyles((r.data || []).filter((s) => !s.archived))).catch(() => {});
  }, []);

  const styleName = (id) => styles.find((s) => s.id === id)?.name || id;
  const toggleStyle = (id) => set("style_ids", form.style_ids?.includes(id) ? form.style_ids.filter((x) => x !== id) : [...(form.style_ids || []), id]);

  const save = async () => {
    try {
      const key = form.key || form.name.toLowerCase().replace(/[^a-z0-9]/g, "");
      const payload = {
        ...form, key,
        price: form.price === "" || form.price === null ? null : Number(form.price),
        style_ids: form.all_designs ? [] : (form.style_ids || []),
      };
      if (editId) await api.put(`/admin/addons/${editId}`, payload);
      else await api.post("/admin/addons", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-kys-ink">Add-Ons</h1><p className="max-w-xl text-sm text-kys-muted">Choose which Style Library designs each add-on can be used with. Customers only see an add-on on its eligible designs.</p></div>
        <button data-testid="new-addon-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-4 py-2 text-sm font-semibold text-white hover:bg-kys-accent"><Plus className="h-4 w-4" /> New</button>
      </div>
      <div className="mt-6 divide-y divide-kys-blush-border rounded-lg border border-kys-blush-border bg-white">
        {items.map((a) => (
          <div key={a.id} className="flex items-center justify-between p-4">
            <div>
              <p className="font-medium text-kys-ink">{a.name}
                <span className="ml-1 text-xs text-kys-muted">({a.all_designs ? "all designs" : `${(a.style_ids || []).length} design${(a.style_ids || []).length === 1 ? "" : "s"}`})</span>
                {a.price != null && <span className="ml-1 font-mono text-xs text-kys-accent">+${Number(a.price).toFixed(2)}</span>}
              </p>
              <p className="text-xs text-kys-muted">{a.description}</p>
              {a.price_note && <p className="text-xs text-kys-accent">{a.price_note}</p>}
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs ${a.enabled ? "text-green-600" : "text-kys-muted"}`}>{a.enabled ? "Enabled" : "Disabled"}</span>
              <button data-testid={`edit-addon-${a.id}`} onClick={() => { setForm({ ...empty, ...a, style_ids: a.style_ids || [], all_designs: a.all_designs !== undefined ? a.all_designs : (a.style_ids || []).length === 0 }); setEditId(a.id); setOpen(true); }} className="text-kys-muted hover:text-kys-accent"><Pencil className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Add-On" : "New Add-On"}>
        <div className="space-y-4">
          <Text label="Name" value={form.name} onChange={(v) => set("name", v)} testid="af-name" />
          <Area label="Description" value={form.description} onChange={(v) => set("description", v)} rows={2} />
          <Text label="Price note (optional)" value={form.price_note} onChange={(v) => set("price_note", v)} hint="e.g. '+$20' — display only, quotes are manual" />
          <Text label="Add-on price ($, optional)" type="number" value={form.price} onChange={(v) => set("price", v)} hint="If set, this amount is added to the style's base price as an estimated starting price." />
          <SingleImageEditor label="Example image" value={form.example_image} onChange={(v) => set("example_image", v)} />
          <Toggle label="Enabled" checked={form.enabled} onChange={(v) => set("enabled", v)} />

          <div className="rounded border border-kys-blush-border p-3">
            <p className="mb-2 text-sm font-medium text-kys-ink">Available for designs</p>
            <Toggle label="All designs (including ones added later)" checked={form.all_designs} onChange={(v) => set("all_designs", v)} testid="af-all-designs" />
            {!form.all_designs && (
              <div className="mt-3">
                <p className="mb-2 text-xs text-kys-muted">Select the specific designs this add-on can be used with:</p>
                <div data-testid="af-design-list" className="max-h-56 space-y-1 overflow-y-auto rounded border border-kys-blush-border p-2">
                  {styles.length === 0 ? <p className="px-1 py-2 text-xs text-kys-muted">No active designs in your Style Library yet.</p> : styles.map((s) => (
                    <label key={s.id} data-testid={`af-design-${s.id}`} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-kys-blush">
                      <input type="checkbox" checked={(form.style_ids || []).includes(s.id)} onChange={() => toggleStyle(s.id)} className="accent-kys-accent" />
                      <span className="text-kys-charcoal">{s.name}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-kys-muted">{(form.style_ids || []).length} selected</p>
              </div>
            )}
          </div>

          <button data-testid="save-addon" onClick={save} className="w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save</button>
        </div>
      </Modal>
    </div>
  );
}
